import { Card } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ChartLine } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { useSelectedModel } from "@/hooks/useSelectedModel";

export const ModelInsights = () => {
  const { model } = useSelectedModel();

  const importance = (model?.feature_importance ?? []) as {
    feature: string;
    importance: number;
  }[];
  const predictions = (model?.predictions ?? []) as {
    row_index: number;
    actual: any;
    predicted: any;
  }[];
  const clusters = (model?.coefficients as any)?.clusters as
    | { cluster: number; size: number; centroid: Record<string, number> }[]
    | null
    | undefined;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <ChartLine className="h-5 w-5" />
        <h2 className="text-xl font-semibold">What the model learned</h2>
      </div>

      {!model ? (
        <EmptyState
          icon={ChartLine}
          title="No model results yet"
          description="Train a model on your own data and the drivers and predictions will appear here."
        />
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium mb-3">Biggest drivers</h3>
            <div className="space-y-2">
              {importance.slice(0, 8).map((f) => (
                <div key={f.feature} className="flex items-center gap-3">
                  <span className="w-40 truncate text-sm">{f.feature}</span>
                  <Progress value={f.importance * 100} className="h-2 flex-1" />
                  <span className="text-xs text-muted-foreground w-12 text-right">
                    {(f.importance * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {clusters && clusters.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3">Groups found</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {clusters.map((c) => (
                  <div key={c.cluster} className="p-3 rounded-lg bg-muted/60">
                    <div className="font-medium text-sm">Group {c.cluster + 1}</div>
                    <div className="text-xs text-muted-foreground">{c.size} records</div>
                    <div className="mt-2 space-y-0.5">
                      {Object.entries(c.centroid)
                        .slice(0, 4)
                        .map(([k, v]) => (
                          <div key={k} className="text-xs flex justify-between gap-2">
                            <span className="truncate text-muted-foreground">{k}</span>
                            <span>{Number(v).toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {predictions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3">
                Predictions on rows the model never saw
              </h3>
              <div className="max-h-72 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      {model.model_type !== "clustering" && <TableHead>Actual</TableHead>}
                      <TableHead>Predicted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {predictions.slice(0, 25).map((p, i) => (
                      <TableRow key={`${p.row_index}-${i}`}>
                        <TableCell>#{p.row_index + 1}</TableCell>
                        {model.model_type !== "clustering" && (
                          <TableCell>{String(p.actual ?? "—")}</TableCell>
                        )}
                        <TableCell className="font-medium">{String(p.predicted)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
